import React from 'react';
import { cn } from "@/lib/utils";
import BreadcrumbNavigation from "@/polymet/components/breadcrumb-navigation";

interface PageLayoutProps {
  children: React.ReactNode;
  className?: string;
  breadcrumbItems?: Array<{ name: string; href: string }>;
  title?: string;
  description?: string;
  headerContent?: React.ReactNode;
  sidebar?: React.ReactNode;
  sidebarPosition?: 'left' | 'right';
  sidebarWidth?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

export default function PageLayout({
  children,
  className = '',
  breadcrumbItems,
  title,
  description,
  headerContent,
  sidebar,
  sidebarPosition = 'left',
  sidebarWidth = 'md',
  fullWidth = false,
}: PageLayoutProps) {
  const sidebarWidthClass = {
    sm: 'w-64',
    md: 'w-72',
    lg: 'w-80',
  }[sidebarWidth];

  return (
    <div className={cn(
      "w-full",
      !fullWidth && "container mx-auto px-4 sm:px-6 lg:px-8",
      className
    )}>
      {/* Breadcrumb - Only show if breadcrumbItems are provided and not empty */}
      {breadcrumbItems && breadcrumbItems.length > 0 && (
        <div className="py-4">
          <BreadcrumbNavigation items={breadcrumbItems} />
        </div>
      )}

      {/* Header Section */}
      {(title || description || headerContent) && (
        <div className="mb-6 sm:mb-8">
          {title && (
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 sm:mb-3">
              {title}
            </h1>
          )}
          {description && (
            <p className="text-base sm:text-lg text-muted-foreground">
              {description}
            </p>
          )}
          {headerContent && (
            <div className="mt-4">
              {headerContent}
            </div>
          )}
        </div>
      )}

      {/* Main Content with Optional Sidebar */}
      <div className={cn(
        "flex flex-col lg:flex-row gap-6 lg:gap-8",
        sidebar && "lg:items-start"
      )}>
        {/* Sidebar */}
        {sidebar && (
          <div className={cn(
            "lg:sticky lg:top-24",
            sidebarWidthClass,
            sidebarPosition === 'right' ? 'lg:order-2' : 'lg:order-1'
          )}>
            {sidebar}
          </div>
        )}

        {/* Main Content */}
        <div className={cn(
          "flex-1",
          sidebarPosition === 'right' ? 'lg:order-1' : 'lg:order-2'
        )}>
          {children}
        </div>
      </div>
    </div>
  );
} 